import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { StatusBadge } from '../../dashboard/components/documents/StatusBadge';
import type { ChildDocumentSummary } from '../api';
import { formatPageRange } from './pageRange';

interface SplitChildrenPanelProps {
  items: ReadonlyArray<ChildDocumentSummary>;
  isLoading: boolean;
}

/** Human label for one child row: its segmenter label, else "Documento N". */
function childTitle(child: ChildDocumentSummary, fallbackIndex: number): string {
  if (child.label !== null && child.label.trim() !== '') {
    return child.label;
  }
  const ordinal = (child.segmentIndex ?? fallbackIndex) + 1;
  return `Documento ${ordinal}`;
}

/** Page-range caption, e.g. "Páginas 3–4" or "Página 5". Empty when the range is unknown. */
function pageRangeCaption(child: ChildDocumentSummary): string {
  if (child.startPage === null || child.endPage === null) {
    return '';
  }
  return formatPageRange(child.startPage, child.endPage);
}

/**
 * Renders the sub-documents a split parent was divided into. Each row links to that child's own
 * reviewer page (`/documents/{childId}`), where it behaves like any independently-submitted job.
 */
export function SplitChildrenPanel({ items, isLoading }: SplitChildrenPanelProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner label="Cargando sub-documentos…" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="card border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        Este documento se marcó como dividido pero todavía no tiene sub-documentos asociados.
      </div>
    );
  }

  return (
    <section
      aria-label="Sub-documentos"
      className="rounded-2xl border border-slate-200/70 bg-white shadow-sm"
    >
      <div className="border-b border-slate-200 px-5 py-3">
        <h2 className="text-base font-semibold text-slate-900">
          {items.length} sub-documentos
        </h2>
        <p className="mt-0.5 text-sm text-slate-500">
          El fichero subido contenía varios documentos. Cada uno se procesa de forma independiente —
          abre uno para revisar sus campos.
        </p>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((child, index) => {
          const caption = pageRangeCaption(child);
          return (
            <li key={child.jobId}>
              <Link
                to={`/documents/${encodeURIComponent(child.jobId)}`}
                className="flex items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-900">
                    {childTitle(child, index)}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {caption !== '' ? <span>{caption}</span> : null}
                    <span className="font-mono">{child.jobId}</span>
                  </div>
                </div>
                <StatusBadge status={child.status} />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
